import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, Archive, ArchiveRestore, ArrowUp, ArrowDown } from "lucide-react";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ProductImages, { AdminImage } from "@/components/ProductImages";

const empty = { name: "", description: "", category_id: "", price: "", size: "", in_stock: true, hidden: false, archived: false, image_url: "", stock_qty: "", sort_order: 0 };
const FIELDS = Object.keys(empty);

export default function Products() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("active");
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/admin/products"), api.get("/admin/categories")]);
    setProducts(p.data);
    setCats(c.data);
  };
  useEffect(() => { load(); }, []);

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";
  const toForm = (p) => ({ ...Object.fromEntries(FIELDS.map((k) => [k, p[k] ?? empty[k]])), price: String(p.price), stock_qty: p.stock_qty ?? "" });

  const openNew = () => { setForm({ ...empty, category_id: cats[0]?.id || "" }); setEditing(null); setOpen(true); };
  const openEdit = (p) => { setForm(toForm(p)); setEditing(p); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.category_id || form.price === "") return toast.error("Name, category and price are required");
    const payload = { ...form, price: parseFloat(form.price), stock_qty: form.stock_qty === "" ? null : parseInt(form.stock_qty, 10) };
    try {
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
        setOpen(false);
      } else {
        const { data } = await api.post("/admin/products", payload);
        toast.success("Product added — you can now upload images");
        setEditing(data);
        setForm(toForm(data));
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const remove = (p) => window.confirm(`Permanently delete "${p.name}"? Past orders keep their details. Consider archiving instead.`) && act(() => api.delete(`/admin/products/${p.id}`), "Product deleted");
  const move = (p, dir) => {
    const ids = products.map((x) => x.id);
    const shownIds = shown.map((x) => x.id);
    const neighbour = shownIds[shownIds.indexOf(p.id) + dir];
    if (!neighbour) return;
    const a = ids.indexOf(p.id), b = ids.indexOf(neighbour);
    [ids[a], ids[b]] = [ids[b], ids[a]];
    act(() => api.post("/admin/products/reorder", { ids }));
  };

  const q = search.trim().toLowerCase();
  const shown = products
    .filter((p) => (filter === "archived" ? p.archived : !p.archived))
    .filter((p) => (filter === "hidden" ? p.hidden : filter === "oos" ? !p.in_stock : true))
    .filter((p) => (catFilter === "all" ? true : p.category_id === catFilter))
    .filter((p) => (q ? `${p.name} ${p.size}`.toLowerCase().includes(q) : true));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Products</h1>
          <p className="mt-1 text-sm text-muted-foreground">{products.filter((p) => !p.archived).length} active · {products.filter((p) => p.archived).length} archived</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product name…" className="w-56" data-testid="product-search" />
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-44" data-testid="product-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="product-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">All active</SelectItem>
              <SelectItem value="hidden">Hidden only</SelectItem>
              <SelectItem value="oos">Out of stock</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openNew} data-testid="add-product-btn" className="rounded-lg bg-primary font-semibold text-primary-foreground transition-colors duration-200 hover:bg-[#7CBCE0]"><Plus className="mr-1.5 h-4 w-4" /> Add Product</Button>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-white">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="overline w-16">Order</TableHead>
            <TableHead className="overline w-14"></TableHead>
            <TableHead className="overline">Name</TableHead>
            <TableHead className="overline">Category</TableHead>
            <TableHead className="overline">Price</TableHead>
            <TableHead className="overline">Qty</TableHead>
            <TableHead className="overline">Stock</TableHead>
            <TableHead className="overline">Visible</TableHead>
            <TableHead className="overline text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.length === 0 && <TableRow><TableCell colSpan={9} className="py-8 text-center text-muted-foreground">No products match.</TableCell></TableRow>}
            {shown.map((p, i) => (
              <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                <TableCell>
                  <div className="flex gap-0.5">
                    <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(p, -1)} data-testid={`move-up-${p.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === shown.length - 1} onClick={() => move(p, 1)} data-testid={`move-down-${p.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
                <TableCell><AdminImage imageId={p.main_image_id} url={p.image_url} alt={p.name} className="h-10 w-10 rounded-md" /></TableCell>
                <TableCell className="font-medium">{p.name} {p.size && <span className="ml-1 text-xs text-muted-foreground">{p.size}</span>}</TableCell>
                <TableCell className="text-muted-foreground">{catName(p.category_id)}</TableCell>
                <TableCell className="font-semibold">{money(p.price)}</TableCell>
                <TableCell className="text-muted-foreground" data-testid={`product-qty-${p.id}`}>{p.stock_qty ?? "—"}</TableCell>
                <TableCell><span className={`text-xs font-semibold ${p.in_stock ? "text-emerald-600" : "text-rose-600"}`}>{p.in_stock ? "In stock" : "Sold out"}</span></TableCell>
                <TableCell><span className={`text-xs font-semibold ${p.hidden ? "text-muted-foreground" : "text-foreground"}`}>{p.archived ? "Archived" : p.hidden ? "Hidden" : "Shown"}</span></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {!p.archived && <Button size="icon" variant="ghost" onClick={() => act(() => api.patch(`/admin/products/${p.id}/toggle`))} data-testid={`toggle-product-${p.id}`} title={p.hidden ? "Show on website" : "Hide from website"}>{p.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => act(() => api.patch(`/admin/products/${p.id}/archive`), p.archived ? "Product restored (still hidden — show it when ready)" : "Product archived")} data-testid={`archive-product-${p.id}`} title={p.archived ? "Restore" : "Archive"}>{p.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} data-testid="product-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="product-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Size / MG</Label><Input value={form.size} onChange={set("size")} data-testid="product-size" placeholder="e.g. 10mg" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Price (AUD)</Label><Input type="number" step="0.01" min="0" value={form.price} onChange={set("price")} data-testid="product-price" /></div>
              <div className="space-y-2"><Label>Stock quantity</Label><Input type="number" min="0" step="1" value={form.stock_qty} onChange={set("stock_qty")} placeholder="Leave blank to not track" data-testid="product-stock-qty" /></div>
            </div>
            <p className="-mt-2 text-xs text-muted-foreground">Quantity is never shown to customers. It goes down when an order is placed, comes back if the order is cancelled, and the product shows as out of stock at 0.</p>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={set("description")} data-testid="product-description" rows={4} /></div>
            <div className="space-y-2">
              <Label>Images</Label>
              {editing ? <ProductImages product={editing} onChange={(p) => { setEditing(p); load(); }} />
                : <p className="rounded-lg bg-secondary/60 px-3 py-2 text-xs text-muted-foreground" data-testid="product-images-save-first">Save the product first, then upload images here.</p>}
            </div>
            <div className="flex flex-wrap items-center gap-8">
              <div className="flex items-center gap-2"><Switch checked={form.in_stock} onCheckedChange={(v) => setForm({ ...form, in_stock: v })} data-testid="product-instock" /><Label>In stock</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.hidden} onCheckedChange={(v) => setForm({ ...form, hidden: v })} data-testid="product-hidden" /><Label>Hidden from website</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="close-product-btn">{editing ? "Close" : "Cancel"}</Button>
            <Button onClick={save} data-testid="save-product-btn" className="bg-primary text-primary-foreground hover:bg-[#7CBCE0]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
